import React, { useState, useContext } from 'react';
import { Badge, Button, Tabs, Tab, Modal, Form } from 'react-bootstrap';
import ProjectContext from '../context/ProjectContext';
import AuthContext from '../context/AuthContext';

// Components
import AccountListSection from '../components/accounts/AccountListSection';
import AccountForm from '../components/accounts/AccountForm';
import AccountEditModal from '../components/accounts/AccountEditModal';
import PlaidDiscoveryModal from '../components/accounts/PlaidDiscoveryModal';
import PlaidConnectionList from '../components/accounts/PlaidConnectionList';

// Hooks
import { useAccounts } from '../hooks/useAccounts';

// QuickBooks Categories order
const TYPE_ORDER = [
  'Bank', 'Accounts receivable (A/R)', 'Other Current Assets', 'Fixed Assets', 'Other Assets',
  'Credit Card', 'Accounts payable (A/P)', 'Other Current Liabilities', 'Long Term Liabilities',
  'Equity', 'Income', 'Other Income', 'Cost of Goods Sold', 'Expenses', 'Other Expense'
];

const Accounts = () => {
  const { activeProject } = useContext(ProjectContext);
  const { user } = useContext(AuthContext);
  
  const { 
    accounts, 
    isEditor, 
    addAccount, 
    updateAccount,
    fetchAccounts
  } = useAccounts(activeProject, user);

  // State
  const [activeTab, setActiveTab] = useState('accounts');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPlaidModal, setShowPlaidModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [editFormData, setEditFormData] = useState({ name: '', type: '', accountNumber: '', description: '' });
  const [updateConnectionId, setUpdateConnectionId] = useState(null);
  const [connectionsRefreshTrigger, setConnectionsRefreshTrigger] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(key);
      setSortOrder('asc');
    }
  };

  if (!activeProject) return <div className="p-5 text-center">Please select or create a project first.</div>;

  // Sort accounts based on QuickBooks order
  const sortHierarchically = (accs) => {
    const result = [];
    const typeGroups = {};
    accs.forEach(acc => {
      if (!typeGroups[acc.type]) typeGroups[acc.type] = [];
      typeGroups[acc.type].push(acc);
    });

    const sortedTypes = Object.keys(typeGroups).sort((a, b) => {
      return TYPE_ORDER.indexOf(a) - TYPE_ORDER.indexOf(b);
    });

    sortedTypes.forEach(type => {
      const typeAccs = typeGroups[type];
      const roots = typeAccs.filter(a => {
        if (!a.parentId) return true;
        const parentIdStr = typeof a.parentId === 'object' ? (a.parentId._id ? a.parentId._id.toString() : '') : a.parentId.toString();
        // If parent is not present in the filtered type list, treat this child as a root
        const hasParentInList = typeAccs.some(x => x._id.toString() === parentIdStr);
        return !hasParentInList;
      });
      
      // Sort roots by account number or name
      roots.sort((a, b) => (a.accountNumber || '').localeCompare(b.accountNumber || '') || a.name.localeCompare(b.name));

      const addChildren = (parent) => {
        result.push(parent);
        const children = typeAccs.filter(a => {
          if (!a.parentId) return false;
          const childParentIdStr = typeof a.parentId === 'object' ? (a.parentId._id ? a.parentId._id.toString() : '') : a.parentId.toString();
          return childParentIdStr === parent._id.toString();
        });
        children.sort((a, b) => (a.accountNumber || '').localeCompare(b.accountNumber || '') || a.name.localeCompare(b.name));
        children.forEach(child => {
          addChildren(child);
        });
      };

      roots.forEach(root => {
        addChildren(root);
      });
    });

    return result;
  };

  const filteredAccounts = accounts.filter(acc => {
    const term = searchTerm.toLowerCase();
    const nameMatch = acc.name.toLowerCase().includes(term);
    const numMatch = acc.accountNumber ? acc.accountNumber.toLowerCase().includes(term) : false;
    const typeMatch = acc.type.toLowerCase().includes(term);
    return nameMatch || numMatch || typeMatch;
  });

  const getSortedAccounts = () => {
    if (!sortBy) {
      return sortHierarchically(filteredAccounts);
    }

    const sorted = [...filteredAccounts];
    sorted.sort((a, b) => {
      let valA = a[sortBy];
      let valB = b[sortBy];

      if (valA === undefined || valA === null) valA = '';
      if (valB === undefined || valB === null) valB = '';

      if (typeof valA === 'number' && typeof valB === 'number') {
        return sortOrder === 'asc' ? valA - valB : valB - valA;
      }

      const strA = String(valA).toLowerCase();
      const strB = String(valB).toLowerCase();
      return sortOrder === 'asc' ? strA.localeCompare(strB) : strB.localeCompare(strA);
    });

    return sorted;
  };

  const sortedAccounts = getSortedAccounts();

  const openEditModal = (acc) => {
    setEditingAccount(acc);
    setEditFormData({
      name: acc.name,
      type: acc.type,
      accountNumber: acc.accountNumber || '',
      description: acc.description || '',
      parentId: acc.parentId || ''
    });
    setShowEditModal(true);
  };

  const handlePlaidUpdate = () => {
    fetchAccounts();
    setConnectionsRefreshTrigger(prev => prev + 1);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    const success = await updateAccount(editingAccount._id, editFormData);
    if (success) setShowEditModal(false);
  };

  const handleToggleActive = async (acc) => {
    const success = await updateAccount(acc._id, {
      name: acc.name,
      type: acc.type,
      accountNumber: acc.accountNumber || '',
      description: acc.description || '',
      parentId: acc.parentId || '',
      isActive: acc.isActive === false ? true : false
    });
    if (success) fetchAccounts();
  };

  const newAccountButton = isEditor ? (
    <Button 
      variant="primary" 
      size="sm" 
      className="font-weight-bold shadow-sm"
      onClick={() => setShowAddModal(true)}
    >
      <i className="fas fa-plus mr-1"></i> New Account
    </Button>
  ) : null;

  const combinedHeaders = [
    { label: 'Account', sortable: true, key: 'name' },
    { label: 'Type', sortable: true, key: 'type' },
    { label: 'QuickBooks Balance', align: 'right', sortable: true, key: 'balance' },
    { label: 'Bank Balance', align: 'right', sortable: true, key: 'bankBalance' },
    ...(isEditor ? [{ label: 'Action', align: 'right', width: '100px' }] : [])
  ];

  return (
    <div className="pb-5">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Chart of Accounts</h1>
        <Badge variant="primary" className="px-3 py-2 shadow-sm" style={{ fontSize: '1rem' }}>
          {activeProject.name}
        </Badge>
      </div>

      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="mb-4 custom-tabs"
      >
        <Tab eventKey="accounts" title={<span><i className="fas fa-list-ul mr-2"></i>Accounts</span>}>
          <div className="mt-3">
            <Form.Group className="mb-3">
              <Form.Control
                type="text"
                placeholder="Search accounts by number, name, or type..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="shadow-sm border-primary"
              />
            </Form.Group>
            <AccountListSection 
              variant="primary"
              type="accounts"
              isEditor={isEditor}
              accounts={sortedAccounts}
              onSubmit={addAccount}
              onEdit={openEditModal}
              onToggleActive={handleToggleActive}
              headers={combinedHeaders}
              extraAction={newAccountButton}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={handleSort}
            />
          </div>
        </Tab>
        <Tab eventKey="connections" title={<span><i className="fas fa-plug mr-2"></i>Bank Connections</span>}>
            <div className="mt-3">
                <PlaidConnectionList 
                    projectId={activeProject._id} 
                    isEditor={isEditor} 
                    onUpdate={handlePlaidUpdate}
                    refreshTrigger={connectionsRefreshTrigger}
                    onDiscoverBank={() => setShowPlaidModal(true)}
                    onUpdateAccounts={(id) => {
                        setUpdateConnectionId(id);
                        setShowPlaidModal(true);
                    }}
                />
            </div>
        </Tab>
      </Tabs>

      <AccountEditModal 
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        onSubmit={handleEditSubmit}
        formData={editFormData}
        setFormData={setEditFormData}
        accounts={accounts}
        editingAccountId={editingAccount?._id}
      />

      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="h5 font-weight-bold">New Account</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <AccountForm 
            accounts={accounts}
            onSubmit={async (formData) => {
              const success = await addAccount(formData);
              if (success) setShowAddModal(false);
            }} 
          />
        </Modal.Body>
      </Modal>

      <PlaidDiscoveryModal 
        show={showPlaidModal}
        onHide={() => {
            setShowPlaidModal(false);
            setUpdateConnectionId(null);
        }}
        projectId={activeProject._id}
        onUpdate={handlePlaidUpdate}
        connectionId={updateConnectionId}
      />
    </div>
  );
};

export default Accounts;
